import { useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import {
  AlignLeft,
  Edit,
  Languages,
  Loader2,
  Maximize2,
  RefreshCw,
  Undo2,
  Wand2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AIChatService } from "@/services/api/AIChatService";
import { LANGUAGE_FLAGS } from "@/utils/localization/language";

interface ContentViewerProps {
  viewMode: "formatted" | "optimized";
  setViewMode: (mode: "formatted" | "optimized") => void;
  urlContent: string | null;
  optimizedContent: string | null;
  isLoading: boolean;
  isOptimizing: boolean;
  isProcessing: boolean;
  processingProgress: number;
  getCurrentPageContent: (content: string | null) => string | null;
  onRefreshContent: () => void;
  loadOptimizedContent: () => Promise<void>;
  onOpenEditor?: () => void;
  onViewModeChange?: (mode: "formatted" | "optimized") => void;
  currentPage?: number;
  currentPart?: number;
}

export const ContentViewer = ({
  viewMode,
  setViewMode,
  urlContent,
  optimizedContent,
  isLoading,
  isOptimizing,
  isProcessing,
  processingProgress,
  getCurrentPageContent,
  onRefreshContent,
  loadOptimizedContent,
  onOpenEditor,
  onViewModeChange,
  currentPage = 1,
  currentPart = 1,
}: ContentViewerProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const [translatedContent, setTranslatedContent] = useState<Record<string, Record<number, string>>>({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const getTranslationKey = () => `${viewMode}_${currentPart}`;
  const getTranslatedContent = () => translatedContent[selectedLanguage]?.[currentPart] ?? null;
  const setTranslatedContentForCurrentPage = (content: string | null) => {
    setTranslatedContent(prev => ({
      ...prev,
      [selectedLanguage]: {
        ...prev[selectedLanguage],
        [currentPart]: content ?? undefined,
      },
    }));
  };
  const unescapeContent = (content: string | null) => {
    if (!content) return null;
    return content.replace(/\\n/g, "\n").replace(/^"|"$/g, "");
  };

  const displayUrlContent = unescapeContent(urlContent);
  const displayOptimizedContent = unescapeContent(optimizedContent);

  const handleTranslate = async () => {
    const contentToTranslate =
      viewMode === "optimized" ? displayOptimizedContent : displayUrlContent;
    if (!contentToTranslate || selectedLanguage === "en") return;

    setIsTranslating(true);
    try {
      const result = await AIChatService.translate({
        text: contentToTranslate,
        targetLanguage:
          LANGUAGE_FLAGS[selectedLanguage as keyof typeof LANGUAGE_FLAGS]?.name || selectedLanguage,
        sourceLanguage: "English",
      });
      setTranslatedContentForCurrentPage(result);
    } catch (error) {
      console.error("Translation failed:", error);
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-6">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-2 border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all hover:scale-105 rounded-lg flex-shrink-0"
            onClick={() => setIsFullscreenOpen(true)}
            disabled={isLoading}
            title="View fullscreen"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          {onOpenEditor && (urlContent || optimizedContent) && (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-2 border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all hover:scale-105 rounded-lg flex-shrink-0"
              onClick={onOpenEditor}
              title="Edit document content"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-2 border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all hover:scale-105 rounded-lg flex-shrink-0"
            onClick={onRefreshContent}
            disabled={isLoading}
            title="Refresh content"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1 sm:gap-2 px-2 border-l border-gray-300 flex-shrink-0">
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="h-8 w-24 sm:w-32 border-2 border-gray-300 text-gray-600 text-xs sm:text-sm">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LANGUAGE_FLAGS)
                  .filter(([code]) => code !== "default")
                  .map(([code, { name }]) => (
                    <SelectItem key={code} value={code}>
                      {name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-2 border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all hover:scale-105 rounded-lg flex-shrink-0"
              onClick={handleTranslate}
              disabled={isTranslating || selectedLanguage === "en"}
              title="Translate content"
            >
              {isTranslating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Languages className="h-4 w-4" />
              )}
            </Button>
            {getTranslatedContent() && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all hover:scale-105 rounded-lg flex-shrink-0"
                      onClick={() => {
                        setTranslatedContentForCurrentPage(null);
                      }}
                    >
                      <Undo2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-gray-800 text-white text-xs">
                    Show Original
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="inline-flex w-fit self-start items-center p-1 border-2 border-gray-200 rounded-xl bg-gray-50 shadow-sm flex-shrink-0 sm:ml-auto">
          <Button
            variant={viewMode === "formatted" ? "default" : "ghost"}
            size="sm"
            className={`h-8 text-[11px] sm:text-xs transition-all duration-200 rounded-lg ${
              viewMode === "formatted"
                ? "bg-genie-primary text-white shadow-md hover:bg-genie-primary-hover"
                : "hover:bg-gray-100"
            }`}
            onClick={() => {
              setViewMode("formatted");
              onViewModeChange?.("formatted");
            }}
            title="Clean, readable version with formatting"
          >
            <AlignLeft className="h-3.5 w-3.5 mr-1" />
            <span className="hidden sm:inline text-xs">Downloaded Text</span>
            <span className="text-xs sm:hidden">Downloaded</span>
          </Button>
          <Button
            variant={viewMode === "optimized" ? "default" : "ghost"}
            size="sm"
            className={`h-8 text-[11px] sm:text-xs transition-all duration-200 rounded-lg ${
              viewMode === "optimized"
                ? "bg-genie-secondary text-white shadow-md hover:bg-genie-secondary-hover"
                : "hover:bg-gray-100"
            }`}
            onClick={async () => {
              if (
                !optimizedContent ||
                optimizedContent ===
                  "No optimized content available. Click 'Optimize' to generate it."
              ) {
                console.log(
                  "Loading optimized content because current content is:",
                  optimizedContent,
                );
                await loadOptimizedContent();
              }
              setViewMode("optimized");
              onViewModeChange?.("optimized");
            }}
            title="AI-enhanced version optimized for knowledge bases"
            disabled={isOptimizing}
          >
            <Wand2 className="h-3.5 w-3.5 mr-1" />
            <span className="hidden text-xs sm:inline">
              {isOptimizing ? "Enhancing..." : "Enhanced for KB"}
            </span>
            <span className="text-xs sm:hidden">{isOptimizing ? "..." : "Enhanced for KB"}</span>
          </Button>
        </div>
      </div>

      {isProcessing && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center mb-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600 mr-2" />
            <p className="text-sm font-medium text-blue-800">Processing PDF...</p>
          </div>
          <div className="h-3 w-full bg-blue-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${processingProgress}%` }}
            />
          </div>
          <p className="text-sm text-blue-700 mt-2">{processingProgress}% complete</p>
          <p className="text-xs text-blue-600 mt-1">
            Large files may take several minutes to parse. You can track the progress here.
          </p>
        </div>
      )}

      <div className="flex-1 min-h-0 w-full overflow-y-auto">
        <div className="rounded-lg border border-[#E5EAF0] bg-white p-6 w-full">
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading content...</span>
            </div>
          ) : viewMode === "formatted" ? (
            <div
              className="w-full max-w-full text-sm leading-relaxed text-gray-800"
              style={{
                wordWrap: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {getTranslatedContent() ? (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                  <p className="text-xs text-blue-600 mb-3">
                    Translated to{" "}
                    {LANGUAGE_FLAGS[selectedLanguage as keyof typeof LANGUAGE_FLAGS]?.name}
                  </p>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-3">{children}</p>,
                      h1: ({ children }) => (
                        <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                      ),
                      h4: ({ children }) => (
                        <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                      ),
                      li: ({ children }) => <li className="mb-1">{children}</li>,
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                          {children}
                        </blockquote>
                      ),
                      code: ({ children }) => (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">{children}</code>
                      ),
                      pre: ({ children }) => (
                        <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                          {children}
                        </pre>
                      ),
                    }}
                  >
                    {getTranslatedContent()}
                  </ReactMarkdown>
                </div>
              ) : (
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="mb-3">{children}</p>,
                    h1: ({ children }) => (
                      <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                        {children}
                      </blockquote>
                    ),
                    code: ({ children }) => (
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">{children}</code>
                    ),
                    pre: ({ children }) => (
                      <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">{children}</pre>
                    ),
                  }}
                >
                  {getCurrentPageContent(displayUrlContent) || "No content available"}
                </ReactMarkdown>
              )}
            </div>
          ) : viewMode === "optimized" ? (
            <div
              className="w-full max-w-full text-sm leading-relaxed text-gray-800"
              style={{
                wordWrap: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {isOptimizing ? (
                <div className="text-center py-12">
                  <img
                    src="/helpgenie_logo.png"
                    alt="Loading..."
                    className="h-16 w-16 mx-auto mb-4 animate-gentle-pulse"
                  />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Enhancing this document...</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    We're optimizing this document for your knowledge base. This reduces content by
                    30-70% while keeping essential information.
                  </p>
                </div>
              ) : optimizedContent &&
                optimizedContent !==
                  "No optimized content available. Click 'Optimize' to generate it." ? (
                <>
                  {getTranslatedContent() ? (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                      <p className="text-xs text-blue-600 mb-3">
                        Translated to{" "}
                        {LANGUAGE_FLAGS[selectedLanguage as keyof typeof LANGUAGE_FLAGS]?.name}
                      </p>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-3">{children}</p>,
                          h1: ({ children }) => (
                            <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                          ),
                          h4: ({ children }) => (
                            <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                          ),
                          ul: ({ children }) => (
                            <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                          ),
                          li: ({ children }) => <li className="mb-1">{children}</li>,
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                              {children}
                            </blockquote>
                          ),
                          code: ({ children }) => (
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                              {children}
                            </code>
                          ),
                          pre: ({ children }) => (
                            <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                              {children}
                            </pre>
                          ),
                        }}
                      >
                        {getTranslatedContent()}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-3">{children}</p>,
                        h1: ({ children }) => (
                          <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                        ),
                        h4: ({ children }) => (
                          <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                        ),
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                            {children}
                          </blockquote>
                        ),
                        code: ({ children }) => (
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                            {children}
                          </code>
                        ),
                        pre: ({ children }) => (
                          <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                            {children}
                          </pre>
                        ),
                      }}
                    >
                      {getCurrentPageContent(displayOptimizedContent)}
                    </ReactMarkdown>
                  )}
                </>
              ) : (
                <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border-2 border-dashed border-amber-300">
                  <div className="bg-amber-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                    <Wand2 className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">
                    Optimization in Progress
                  </h3>
                  <p className="text-amber-700 mb-4 max-w-md mx-auto font-medium">
                    This document is being optimized for your knowledge base.
                  </p>
                  <p className="text-amber-600 text-sm max-w-md mx-auto">
                    This may take a few moments. The enhanced version will appear here once ready.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div
              className="w-full max-w-full text-sm leading-relaxed text-gray-800"
              style={{
                wordWrap: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-3">{children}</p>,
                  h1: ({ children }) => (
                    <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                  ),
                  h2: ({ children }) => <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>,
                  h3: ({ children }) => (
                    <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                  ),
                  h4: ({ children }) => (
                    <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                  ),
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                      {children}
                    </blockquote>
                  ),
                  code: ({ children }) => (
                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">{children}</code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">{children}</pre>
                  ),
                }}
              >
                {getCurrentPageContent(displayUrlContent) || "No content available"}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {isFullscreenOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/75 z-[200]"
              onClick={() => setIsFullscreenOpen(false)}
            />
            <div className="fixed inset-0 z-[200] flex flex-col">
              <div className="flex items-center justify-between p-4 bg-white border-b">
                <h2 className="text-lg font-semibold text-gray-900">
                  {viewMode === "formatted" ? "Downloaded Text" : "Enhanced for KB"}
                </h2>
                <button
                  onClick={() => setIsFullscreenOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto bg-white p-8">
                <div className="w-full max-w-4xl mx-auto text-sm leading-relaxed text-gray-800">
                  {viewMode === "formatted" ? (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-3">{children}</p>,
                        h1: ({ children }) => (
                          <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                        ),
                        h4: ({ children }) => (
                          <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                        ),
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                            {children}
                          </blockquote>
                        ),
                        code: ({ children }) => (
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                            {children}
                          </code>
                        ),
                        pre: ({ children }) => (
                          <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                            {children}
                          </pre>
                        ),
                      }}
                    >
                      {getCurrentPageContent(
                        getTranslatedContent() ? getTranslatedContent() : displayUrlContent,
                      ) || "No content available"}
                    </ReactMarkdown>
                  ) : (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-3">{children}</p>,
                        h1: ({ children }) => (
                          <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>
                        ),
                        h4: ({ children }) => (
                          <h4 className="text-base font-semibold mb-2 mt-2">{children}</h4>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
                        ),
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3">
                            {children}
                          </blockquote>
                        ),
                        code: ({ children }) => (
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                            {children}
                          </code>
                        ),
                        pre: ({ children }) => (
                          <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                            {children}
                          </pre>
                        ),
                      }}
                    >
                      {getCurrentPageContent(
                        getTranslatedContent()
                          ? getTranslatedContent()
                          : displayOptimizedContent,
                      )}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
